import { useQuery } from '@apollo/client';
import { AccountBalance as AccountIcon } from '@mui/icons-material';
import {
  Box,
  ButtonBase,
  Drawer,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
} from '@mui/material';
import _isEqual from 'lodash/isEqual';
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { FixedSizeList } from 'react-window';
import { makeStyles } from 'tss-react/mui';

// import svg icons
import {
  Add as AddIcon,
  ArrowDropDown,
  ArrowDropUp,
  Close as CloseIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  FilterAlt as FilterIcon,
  Search as SearchIcon,
} from '@mui/icons-material';

// relative imports
import { creatingAccountVar } from '../../../../data/apollo/cache/reactiveVars';
import { GET_PREPARER_LINKS } from '../../../../data/apollo/queries';
import { getApplicationTypeFilterFieldFromConfig } from '../../../../data/app-config/configs/applicationTypeField-utilities';
import { useAccountConfig } from '../../../providers/AccountConfigProvider';
import { useAccount } from '../../../providers/AccountProvider';
import AutoCompleteTextField from '../../custom-components/AutoCompleteTextField';
import LedgibleButton from '../../custom-components/buttons/LedgibleButton';
import useAlternativeDisplay from '../../custom-hooks/useAlternativeDisplay';
import { useDisplayConfig } from '../../custom-hooks/useDisplayConfig';
import ActiveAccountIndicator from './ActiveAccountIndicator';
import VirtualizedAccountRow from './VirtualizedAccountRow';

const useStyles = makeStyles()((theme, { isMobile }) => ({
  windowWrapper: {
    display: 'flex',
    height: '100%',
    borderLeft: `${isMobile ? 0 : 1}px solid ${theme.palette.grey[200]}`,
    paddingLeft: isMobile ? 0 : theme.spacing(2),
    marginLeft: isMobile ? 0 : theme.spacing(3),
    alignItems: 'center',
  },
  accountListSecondary: {
    fontStyle: 'italic',
  },
  accountButton: {
    maxWidth: 180,
    minWidth: 100,
    maxHeight: 32,
    borderRadius: 4,
    backgroundColor:
      theme.whiteLabel.AccountBox.color || 'rgba(255, 255, 255, 0.1)',
    color: theme.whiteLabel.AccountBox.textColor || 'inherit',
    padding: theme.spacing(0.25, 1),
  },
  accountButtonListText: {
    color:
      theme.whiteLabel.HeaderText.color || isMobile
        ? undefined
        : theme.palette.common.white,
  },
  dropdownIcon: {
    marginLeft: theme.spacing(0.75),
    color:
      theme.whiteLabel.HeaderText.color || isMobile
        ? undefined
        : theme.palette.common.white,
  },
  headerText: {
    color:
      theme.whiteLabel.AccountWindow.headerTextColor ||
      theme.palette.common.white,
    fontSize: theme.typography.pxToRem(24),
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  tealText: {
    color: theme.palette.primary.main,
  },
  drawerHeader: {
    padding: theme.spacing(2),
    background:
      theme.whiteLabel.AccountWindow.color || theme.palette.primary.main,
  },
  accountAvatar: {
    marginRight: 10,
    color: '#fff',
  },
  activeAccountButton: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  displaySet: {
    display: 'block',
  },
  searchBox: {
    flexGrow: 1,
  },
  selectField: {
    marginTop: 0,
    '& .MuiOutlinedInput-root': {
      borderRadius: 0,
    },
  },
  searchAdornment: {
    color: theme.palette.grey[400],
  },
  searchAdornmentActive: {
    color: theme.palette.secondary.light,
  },
  filterButton: {
    borderRadius: 25,
  },
  filterInputs: {
    backgroundColor: 'white',
  },
  filterDiv: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderBottom: `1px solid ${theme.palette.primary.main}`,
  },
}));

/* reducer default account state */
function heightsReducer(state, [element, value]) {
  switch (element) {
    case 'drawer':
      return { ...state, drawer: value };
    case 'search':
      return { ...state, search: value };
    case 'filters':
      return { ...state, filters: value };
    case 'header':
      return { ...state, header: value };
    default:
      return state;
  }
}

const filterCallback = (state, filterKey, filterValue) => ({
  ...state,
  [filterKey]: filterValue,
});

/* reducer account filter state */
function filtersReducer(state, [filter, value]) {
  if (filter) {
    const newState = filterCallback(state, filter, value);
    return newState;
  }
  return state;
}

const SelectAccountDisplay = () => {
  const { isMobile } = useAlternativeDisplay();
  const { classes, cx } = useStyles({ isMobile });
  const {
    currentAccount,
    accounts,
    selectAccount,
    clientPermissions: permissions,
    loadingAccountInfo,
  } = useAccount();
  const { accountConfig = {}, clearConfigError } = useAccountConfig();
  const { clientLabel } = useDisplayConfig(accountConfig);
  // Get account selection config
  const {
    accountSelection: {
      availableFilters,
      enableFiltering,
      secondaryLabelFieldId,
    } = {},
  } = accountConfig;

  const defaultFilters = useMemo(() => {
    const filters = { accountSort: 'asc' };
    if (availableFilters && availableFilters.length) {
      availableFilters.forEach((filter) => {
        filters[filter.id] = filter.defaultValue;
      });
    }
    return filters;
  }, [availableFilters]);

  // Unified list state
  const [filteredList, setFilteredList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [listOffset, setListOffset] = useState(0);
  const [searchActive, setSearchActive] = useState(false);

  // UI state
  const [accountName, setAccountName] = useState(null);
  const [accountNameInputValue, setAccountNameInputValue] = useState('');
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [accountDropdownOpen, setAccountDropdownOpen] = useState(false);
  const [dropdownAnchorEl, setDropdownAnchorEl] = useState(null);
  const [windowWidth, setWindowWidth] = useState(450);
  const [windowHeight, setWindowHeight] = useState(800);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  // Unified search handlers with proper debouncing
  const searchTimeoutRef = useRef(null);

  // Search state
  const [searchTerm, setSearchTerm] = useState(null);
  const [searchTermText, setSearchTermText] = useState('');

  const clientListLimit = 15;

  // state for ref'ed heights
  const [heights, dispatchHeights] = useReducer(heightsReducer, {
    drawer: 0,
    header: 0,
    filters: 0,
    search: 0,
    addButton: 0,
  });

  // filter state for accounts with application type enabled
  const titleRef = useRef(null);
  const lastLoadTimeRef = useRef(0);
  const [appliedFilters, setAppliedFilters] = useState(defaultFilters);
  const [selectedFilters, dispatchSelected] = useReducer(
    filtersReducer,
    defaultFilters,
  );
  const [showFilters, setShowFilters] = useState(false);

  // static heights
  const LIST_ITEM_HEIGHT = 75;
  const canCreateAccount = currentAccount.accountingAdd;

  // Check if there are any menu items to show
  const hasMenuItems =
    (!permissions.isAdminViewingClient && canCreateAccount) || isMobile;

  // Initialize filters
  useEffect(() => {
    setAppliedFilters(defaultFilters);
    dispatchSelected(['default']);
  }, [defaultFilters]);

  // Cleanup timeout on unmount
  useEffect(
    () => () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    },
    [],
  );

  // Handle window resize for list width and height
  useEffect(() => {
    const updateWindowDimensions = () => {
      setWindowWidth(window.innerWidth > 450 ? 450 : window.innerWidth);
      setWindowHeight(window.innerHeight);
    };

    // Set initial dimensions
    updateWindowDimensions();

    // Add resize listener
    window.addEventListener('resize', updateWindowDimensions);

    return () => {
      window.removeEventListener('resize', updateWindowDimensions);
    };
  }, []);

  // handle open/close of account dropdown
  const handleDropdownClick = (e) => {
    if (dropdownAnchorEl) {
      setAccountDropdownOpen(false);
      setDropdownAnchorEl(null);
    } else {
      setDropdownAnchorEl(e.currentTarget);
      setAccountDropdownOpen(true);
    }
  };

  // callback ref for height management
  const measuredRef = useCallback((node) => {
    if (node !== null) {
      let elementId;
      if (node.id === 'account-window-search') {
        elementId = 'search';
      }
      if (node.id === 'account-window-filters') {
        elementId = 'filters';
      }
      if (node.id === 'account-window-drawer') {
        elementId = 'drawer';
      }
      if (node.id === 'account-window-header') {
        elementId = 'header';
        titleRef.current = node;
      }
      if (elementId) {
        // Measure height immediately to avoid performance issues
        const { height } = node.getBoundingClientRect() || {};
        dispatchHeights([elementId, height]);
      }
    }
  }, []);

  const toggleDrawer = useCallback(() => {
    setAccountMenuOpen(!accountMenuOpen);
    if (accountMenuOpen && showFilters) {
      setShowFilters(false);
    }
  }, [accountMenuOpen, showFilters]);

  const handleApplyFilters = () => {
    setShowFilters(false);
    setAppliedFilters(selectedFilters);
  };

  const handleResetFilters = () => {
    setShowFilters(false);
    setAppliedFilters(defaultFilters);
    dispatchSelected(['default']);
  };

  const chooseAccount = useCallback(
    (accountId) => {
      clearConfigError();
      selectAccount(accountId);
      toggleDrawer();
    },
    [clearConfigError, selectAccount, toggleDrawer],
  );

  const handleNameFilter = (e, value) => {
    setAccountName(value);
  };

  const isDefaultFilter = useMemo(() => {
    if (_isEqual(defaultFilters, appliedFilters)) return true;
    return false;
  }, [appliedFilters, defaultFilters]);

  const handleSearchTermChange = useCallback(
    (val) => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      searchTimeoutRef.current = setTimeout(() => {
        setSearchActive(true);
        setSearchTerm(val);
        if (listOffset !== 0) {
          setListOffset(0);
        }
      }, 200);
    },
    [listOffset],
  );

  const handleSearchTermTextChange = (e) => {
    setSearchTermText(e.target.value);
    handleSearchTermChange(e.target.value);
  };

  const handleSearchTermTextKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearchTermChange(e);
    }
  };

  const handleClearSearch = useCallback(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    setSearchTermText('');
    setSearchActive(false);
    setSearchTerm('');
    setListOffset(0);
  }, []);

  // Memoize the query skip condition to prevent unnecessary re-renders
  const shouldSkipQuery = useMemo(
    () => !accountMenuOpen || !permissions.isAdminViewingClient,
    [accountMenuOpen, permissions.isAdminViewingClient],
  );

  // update displayed list based on selection for regular accounts
  useEffect(() => {
    if (!permissions.isAdminViewingClient) {
      let visibleAccounts = accounts
        .filter((account) => {
          if (accountName && accountName !== account.name) return false;
          return true;
        })
        .sort((a, b) => a.name.localeCompare(b.name));

      if (appliedFilters.accountSort === 'desc') {
        visibleAccounts = visibleAccounts.reverse();
      }

      const applicationTypeFilters = Object.keys(appliedFilters).filter(
        (key) => key !== 'accountSort',
      );

      applicationTypeFilters.forEach((applicationTypeFilter) => {
        if (appliedFilters[applicationTypeFilter] !== defaultFilters[applicationTypeFilter]) {
          visibleAccounts = visibleAccounts.filter(
            (account) =>
              account.applicationTypeFields?.[applicationTypeFilter] === appliedFilters[applicationTypeFilter],
          );
        }
      });

      setFilteredList(visibleAccounts);
      setTotalCount(visibleAccounts.length);
    }
  }, [
    accounts,
    accountName,
    appliedFilters,
    defaultFilters,
    permissions.isAdminViewingClient,
  ]);

  // set height of list to properly size window for virtualization
  const listHeight = useMemo(() => {
    // Ensure we have valid drawer height before calculating
    if (heights.drawer <= 0) {
      return 0;
    }

    const nonListHeight =
      heights.search +
      heights.header +
      heights.addButton +
      (showFilters ? heights.filters : 0);

    const heightDifference = heights.drawer - nonListHeight - 1;

    // Ensure minimum height, but allow up to 90% of window height for better UX
    const minHeight = 100;
    const maxHeight = Math.floor(windowHeight * 0.9);

    return Math.max(minHeight, Math.min(maxHeight, heightDifference));
  }, [heights, showFilters, windowHeight]);

  // Memoize query variables to prevent unnecessary re-renders
  const queryVariables = useMemo(
    () => ({
      accountId: currentAccount.preparerLink?.preparerAccountId,
      filters: [
        {
          id: 'accountLinkStatus',
          arrayValue: ['connected', 'preparer-only'],
        },
      ],
      clientAccountName: searchTerm,
      limit: clientListLimit,
      offset: listOffset,
      sortBy: [{ id: 'clientAccountName', desc: false }],
    }),
    [currentAccount.preparerLink?.preparerAccountId, searchTerm, listOffset],
  );

  // Get data for client account display in infinite scrolling list
  const {
    previousData: {
      getPreparerLinksByFilter: { totalFiltered: previousTotal } = {},
    } = {},
    data: { getPreparerLinksByFilter: { totalFiltered } = {} } = {},
  } = useQuery(GET_PREPARER_LINKS, {
    variables: queryVariables,
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    onCompleted(result) {
      const { preparerLinks = [], totalFiltered: resultTotalFiltered = 0 } =
        result?.getPreparerLinksByFilter || {};

      // Safety check to prevent crashes
      if (!Array.isArray(preparerLinks)) {
        console.warn('preparerLinks is not an array:', preparerLinks);
        setIsLoadingMore(false);
        return;
      }

      const page = Math.floor(resultTotalFiltered / clientListLimit);
      const atLimit = page > 0 && resultTotalFiltered < page * clientListLimit;

      if (searchActive && listOffset === 0) {
        setFilteredList([...preparerLinks]);
        setTotalCount(resultTotalFiltered);
        if (!searchTerm) {
          setSearchActive(false);
        }
      } else if (
        preparerLinks.length > 0 &&
        !filteredList.map(({ id }) => id).includes(preparerLinks[0]?.id) &&
        !atLimit
      ) {
        setFilteredList((current) => [...current, ...preparerLinks]);
      }

      // Reset loading state when query completes
      setIsLoadingMore(false);
    },
    skip: shouldSkipQuery,
  });

  // Handle infinite scroll for client accounts
  const handleItemsRendered = useCallback(
    ({ visibleStopIndex }) => {
      // Safety checks
      if (
        !Array.isArray(filteredList) ||
        typeof visibleStopIndex !== 'number'
      ) {
        return;
      }

      // Only trigger loading when we're at the end of a complete page and approaching the next page
      const isAtEndOfCurrentPage = visibleStopIndex >= filteredList.length - 1;
      const hasMoreData =
        filteredList.length < (totalFiltered ?? previousTotal);
      const isCompletePage = filteredList.length % clientListLimit === 0;

      const shouldLoadMore =
        permissions.isAdminViewingClient &&
        isAtEndOfCurrentPage &&
        hasMoreData &&
        isCompletePage &&
        !searchActive &&
        !isLoadingMore; // Prevent duplicate requests

      if (shouldLoadMore) {
        const now = Date.now();
        const timeSinceLastLoad = now - lastLoadTimeRef.current;

        // Debounce: only allow loading if it's been at least 500ms since last load
        if (timeSinceLastLoad > 500) {
          lastLoadTimeRef.current = now;
          setIsLoadingMore(true);
          setListOffset(filteredList.length);
        }
      }
    },
    [
      permissions.isAdminViewingClient,
      filteredList,
      totalFiltered,
      previousTotal,
      searchActive,
      clientListLimit,
      isLoadingMore,
    ],
  );

  // Safety check - don't render if critical data is missing
  if (!currentAccount || !accountConfig?.id || loadingAccountInfo) {
    return null;
  }

  return (
    <div className={classes.windowWrapper}>
      <AccountIcon
        className={classes.greyText}
        fontSize="small"
        sx={(theme) => ({
          mr: 1,
          color: theme.palette.grey[isMobile ? 600 : 300],
          display: 'block',
        })}
      />
      <ButtonBase
        onClick={toggleDrawer}
        className={cx('accounts-window', classes.accountButton)}
      >
        <ActiveAccountIndicator circleSize={8} />
        <ListItemText
          primary={currentAccount.id && currentAccount.name}
          classes={{
            root: classes.accountButtonListText,
          }}
          primaryTypographyProps={{
            variant: 'body1',
            noWrap: true,
            color: 'inherit',
          }}
        />
        {accountMenuOpen && (
          <ArrowDropUp className={classes.dropdownIcon} fontSize="small" />
        )}
        {!accountMenuOpen && (
          <ArrowDropDown className={classes.dropdownIcon} fontSize="small" />
        )}
      </ButtonBase>
      <Drawer
        anchor="left"
        variant="temporary"
        open={accountMenuOpen}
        onClose={toggleDrawer}
        id="account-window-drawer"
        ref={measuredRef}
      >
        <Box
          className={classes.drawerHeader}
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          id="account-window-header"
          ref={measuredRef}
        >
          {hasMenuItems ? (
            <span
              className={classes.headerText}
              aria-label="toggle account menu"
              onClick={handleDropdownClick}
              role="button"
              id="account-window-actions-menu"
              tabIndex={0}
              onKeyDown={handleDropdownClick}
            >
              {permissions.isAdminViewingClient
                ? `${clientLabel} Accounts`
                : 'Accounts'}
              {accountDropdownOpen && (
                <ArrowDropUp
                  className={classes.dropdownIcon}
                  fontSize="medium"
                />
              )}
              {!accountDropdownOpen && (
                <ArrowDropDown
                  className={classes.dropdownIcon}
                  fontSize="medium"
                />
              )}
            </span>
          ) : (
            <span className={classes.headerText}>
              {permissions.isAdminViewingClient
                ? `${clientLabel} Accounts`
                : 'Accounts'}
            </span>
          )}
          {hasMenuItems && (
            <Menu
              id="account-dropdown-menu"
              anchorEl={dropdownAnchorEl}
              open={accountDropdownOpen}
              onClose={handleDropdownClick}
            >
              {/* case where account creation is enabled */}
              {!permissions.isAdminViewingClient && canCreateAccount && (
                <Tooltip title={permissions.disabledText.accountAdd}>
                  <MenuItem
                    onClick={() => {
                      handleDropdownClick();
                      creatingAccountVar(true);
                    }}
                    disabled={!permissions.accountAdd}
                    id="create-account-button-add"
                  >
                    <ListItemIcon>
                      <AddIcon fontSize="medium" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Add New Account"
                      primaryTypographyProps={{
                        noWrap: true,
                        color: 'inherit',
                      }}
                    />
                  </MenuItem>
                </Tooltip>
              )}
              {isMobile && (
                <MenuItem onClick={toggleDrawer}>
                  <ListItemIcon>
                    <CloseIcon fontSize="medium" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Close"
                    primaryTypographyProps={{
                      noWrap: true,
                      color: 'inherit',
                    }}
                  />
                </MenuItem>
              )}
            </Menu>
          )}
          {!permissions.isAdminViewingClient && enableFiltering && (
            <LedgibleButton
              id="search-accounts-type-filter-btn"
              variant="outlined"
              color="white"
              className={classes.filterButton}
              aria-label="Account Filters"
              onClick={() => setShowFilters((current) => !current)}
              textTransformOff
              startIcon={
                <FilterIcon
                  size="small"
                  className={
                    !isDefaultFilter ? classes.searchAdornmentActive : null
                  }
                />
              }
              endIcon={
                showFilters ? (
                  <ExpandLessIcon size="small" />
                ) : (
                  <ExpandMoreIcon size="small" />
                )
              }
            >
              Filter
            </LedgibleButton>
          )}
        </Box>
        {!permissions.isAdminViewingClient && showFilters && (
          <div
            id="account-window-filters"
            className={classes.filterDiv}
            ref={measuredRef}
          >
            <TextField
              id="account-sort-input"
              select
              label="Sort By"
              value={selectedFilters.accountSort}
              onChange={(e) =>
                dispatchSelected(['accountSort', e.target.value])
              }
              name="accountSort"
              variant="outlined"
              fullWidth
              margin="dense"
              SelectProps={{
                className: classes.filterInputs,
              }}
            >
              <MenuItem value="asc">Account Name: A - Z</MenuItem>
              <MenuItem value="desc">Account Name: Z - A</MenuItem>
            </TextField>
            {availableFilters.map((filter) => (
              <Fragment key={filter.id}>
                {getApplicationTypeFilterFieldFromConfig(filter.id, {
                  id: `account-${filter.id}-filter`,
                  variant: 'outlined',
                  SelectProps: {
                    className: classes.filterInputs,
                  },
                  value: selectedFilters[filter.id],
                  onChange: (e) =>
                    dispatchSelected([filter.id, e.target.value]),
                })}
              </Fragment>
            ))}
            <Box
              width="100%"
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
            >
              <LedgibleButton
                id="account-filter-clear-button"
                size="small"
                color="secondary"
                variant="outlined"
                textTransformOff
                onClick={handleResetFilters}
                marginTop={8}
                marginRight={16}
              >
                Clear
              </LedgibleButton>
              <LedgibleButton
                id="account-filter-apply-button"
                size="small"
                variant="contained"
                color="primary"
                textTransformOff
                onClick={handleApplyFilters}
                marginTop={8}
              >
                Apply
              </LedgibleButton>
            </Box>
          </div>
        )}
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="stretch"
          id="account-window-search"
          ref={measuredRef}
        >
          {permissions.isAdminViewingClient ? (
            <TextField
              value={searchTermText}
              variant="outlined"
              placeholder="Search Client Accounts..."
              className={classes.selectField}
              margin="none"
              InputProps={{
                startAdornment: (
                  <InputAdornment
                    position="start"
                    className={classes.searchAdornment}
                  >
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchTermText && (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleClearSearch}
                      className={classes.searchIconButton}
                      aria-label="directions"
                      size="small"
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onChange={handleSearchTermTextChange}
              onKeyDown={handleSearchTermTextKeyDown}
              fullWidth
            />
          ) : (
            <AutoCompleteTextField
              virtualize
              value={accountName}
              inputValue={accountNameInputValue}
              optionsList={accounts.map(({ name }) => name)}
              handleChange={handleNameFilter}
              handleInputChange={(event, newInputValue) => {
                setAccountNameInputValue(newInputValue);
              }}
              isOptionEqualToValue={(option, value) => option === value}
              TextFieldProps={{
                variant: 'outlined',
                placeholder: 'Search Accounts...',
                className: classes.selectField,
                margin: 'none',
                InputProps: {
                  startAdornment: (
                    <InputAdornment
                      position="start"
                      className={classes.searchAdornment}
                    >
                      <SearchIcon />
                    </InputAdornment>
                  ),
                },
              }}
              className={classes.searchBox}
              inputProps={{
                name: 'accountName',
                id: 'accountName',
              }}
            />
          )}
        </Box>
        {filteredList.length > 0 && listHeight > 0 && (
          <div>
            <FixedSizeList
              height={listHeight}
              width={windowWidth}
              itemSize={LIST_ITEM_HEIGHT}
              itemCount={
                permissions.isAdminViewingClient
                  ? totalFiltered ?? previousTotal
                  : totalCount
              }
              itemData={filteredList}
              onItemsRendered={handleItemsRendered}
            >
              {({ index, style, data }) => (
                <VirtualizedAccountRow
                  index={index}
                  style={style}
                  data={data}
                  currentAccount={currentAccount}
                  chooseAccount={chooseAccount}
                  toggleDrawer={toggleDrawer}
                  secondaryLabelFieldId={secondaryLabelFieldId}
                  isAdminViewingClient={permissions.isAdminViewingClient}
                  classes={classes}
                />
              )}
            </FixedSizeList>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default SelectAccountDisplay;
